import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { Company } from "../../api/fetchMyCompanies/types";
import { fetchMyCompanies } from "../../api/fetchMyCompanies/fetchMyCompaniesApi";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useLocation } from "react-router-dom";
import { getSectionFromPath } from "@/utils/navigationHelpers";
import { cn } from "@/utils/utils";

interface CompanySwitcherModalProps {
  open: boolean;
  onClose: () => void;
}

const CompanySwitcherModal: React.FC<CompanySwitcherModalProps> = ({
  open,
  onClose,
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const debouncedSearch = useDebouncedValue(search, 500);

  const location = useLocation();

  useEffect(() => {
    if (open) {
      setCompanies([]);
      setPage(1);
      setHasMore(true);
      setIsInitialLoading(true);
      loadCompanies(1, debouncedSearch, true);
    }
  }, [debouncedSearch, open]);

  useEffect(() => {
    if (!open) {
      setCompanies([]);
      setPage(1);
      setHasMore(true);
      setSelectedCompany(null);
      setSearch("");
      setIsInitialLoading(false);
      setIsLoadingMore(false);
    }
  }, [open]);

  const loadCompanies = (
    pageNumber: number,
    searchQuery: string,
    isInitial: boolean = false,
  ) => {
    if (!isInitial) {
      setIsLoadingMore(true);
    }

    fetchMyCompanies(pageNumber, searchQuery)
      .then((response) => {
        const newCompanies = response.data;

        setCompanies((prevCompanies) =>
          pageNumber === 1 ? newCompanies : [...prevCompanies, ...newCompanies],
        );

        if (newCompanies.length < 10) {
          setHasMore(false);
        }
        setIsInitialLoading(false);
        setIsLoadingMore(false);
      })
      .catch((error) => {
        console.error("Error fetching companies:", error);
        setHasMore(false);
        setIsInitialLoading(false);
        setIsLoadingMore(false);
      });
  };

  const fetchNextCompanies = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadCompanies(nextPage, debouncedSearch, false);
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
  };

  const handleConfirmSelection = () => {
    if (selectedCompany) {
      localStorage.setItem("selectedCompanyUuid", selectedCompany.companyUuid);

      const sectionInfo = getSectionFromPath(location.pathname);
      if (sectionInfo) {
        window.location.href = sectionInfo.defaultRoute;
      } else {
        window.location.href = "/";
      }

      onClose();
    }
  };

  // Show initial loading state
  if (isInitialLoading) {
    return (
      <Dialog className="relative z-50" onClose={onClose} open={open}>
        <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200" />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-2xl transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200">
              {/* Custom Close Button */}
              <button
                aria-label="Close dialog"
                className="absolute right-4 top-4 z-10 group"
                onClick={onClose}
              >
                <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 transition-all duration-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:scale-110 hover:rotate-90 active:scale-95">
                  <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 transition-all duration-300 group-hover:scale-110" />
                </div>
              </button>

              {/* Header */}
              <div className="border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-900 px-6 py-5">
                <div className="flex items-center space-x-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 backdrop-blur-sm">
                    <BuildingOffice2Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                      Switch Company
                    </DialogTitle>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Select the company you want to work with
                    </p>
                  </div>
                </div>
              </div>

              {/* Search Bar */}
              <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-4 py-2.5 text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search companies by name or ID..."
                    type="text"
                    value={search}
                  />
                </div>
              </div>

              {/* Loading State */}
              <div className="flex h-[400px] items-center justify-center">
                <div className="flex flex-col items-center">
                  <ArrowPathIcon className="h-8 w-8 animate-spin text-primary" />
                  <span className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    Loading companies...
                  </span>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-6 py-4">
                <div className="flex gap-3">
                  <button
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-[1.02] active:scale-[0.98]"
                    onClick={onClose}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 rounded-lg bg-gray-300 dark:bg-gray-700 cursor-not-allowed opacity-50 px-4 py-2.5 text-sm font-semibold text-white"
                    disabled
                    type="button"
                  >
                    Select a Company
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog className="relative z-50" onClose={onClose} open={open}>
      <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200" />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-2xl transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200">
            {/* Custom Close Button */}
            <button
              aria-label="Close dialog"
              className="absolute right-4 top-4 z-10 group"
              onClick={onClose}
            >
              <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 transition-all duration-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:scale-110 hover:rotate-90 active:scale-95">
                <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 transition-all duration-300 group-hover:scale-110" />
              </div>
            </button>

            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-900 px-6 py-5">
              <div className="flex items-center space-x-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 backdrop-blur-sm">
                  <BuildingOffice2Icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                    Switch Company
                  </DialogTitle>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Select the company you want to work with
                  </p>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-4 py-2.5 text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search companies by name or ID..."
                  type="text"
                  value={search}
                />
              </div>
            </div>

            {/* Company List */}
            <div className="px-6 py-4">
              <InfiniteScroll
                className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent"
                dataLength={companies.length}
                endMessage={
                  companies.length > 0 ? (
                    <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No more companies to load
                    </div>
                  ) : !isLoadingMore ? (
                    <div className="py-8 text-center">
                      <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-700" />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        No companies found
                      </p>
                    </div>
                  ) : null
                }
                hasMore={hasMore && !isLoadingMore}
                height={400}
                loader={
                  isLoadingMore ? (
                    <div className="flex items-center justify-center py-4">
                      <ArrowPathIcon className="h-5 w-5 animate-spin text-primary" />
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        Loading more companies...
                      </span>
                    </div>
                  ) : null
                }
                next={fetchNextCompanies}
              >
                <div className="space-y-2">
                  {companies.map((company) => (
                    <button
                      className={cn(
                        "group relative w-full rounded-xl border-2 p-4 text-left transition-all duration-200",
                        selectedCompany?.companyUuid === company.companyUuid
                          ? "border-primary bg-primary/5 dark:bg-primary/10"
                          : "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50",
                      )}
                      key={company.companyUuid}
                      onClick={() => handleCompanySelect(company)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={cn(
                              "flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200",
                              selectedCompany?.companyUuid ===
                                company.companyUuid
                                ? "bg-primary/20"
                                : "bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700",
                            )}
                          >
                            <BuildingOffice2Icon
                              className={cn(
                                "h-5 w-5 transition-colors duration-200",
                                selectedCompany?.companyUuid ===
                                  company.companyUuid
                                  ? "text-primary"
                                  : "text-gray-600 dark:text-gray-400",
                              )}
                            />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {company.companyName}
                            </div>
                            {company.intacctId && (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Intacct ID: {company.intacctId}
                              </div>
                            )}
                          </div>
                        </div>
                        {selectedCompany?.companyUuid ===
                          company.companyUuid && (
                          <CheckCircleIcon className="h-5 w-5 text-primary animate-in zoom-in-50 duration-200" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </InfiniteScroll>
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-6 py-4">
              <div className="flex gap-3">
                <button
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-[1.02] active:scale-[0.98]"
                  onClick={onClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={cn(
                    "flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200",
                    selectedCompany
                      ? "bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/20"
                      : "bg-gray-300 dark:bg-gray-700 cursor-not-allowed opacity-50",
                  )}
                  disabled={!selectedCompany}
                  onClick={handleConfirmSelection}
                  type="button"
                >
                  {selectedCompany ? (
                    <span className="flex items-center justify-center">
                      <CheckCircleIcon className="mr-2 h-4 w-4" />
                      Switch to {selectedCompany.companyName}
                    </span>
                  ) : (
                    "Select a Company"
                  )}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default CompanySwitcherModal;
